import React from "react";
import axios, { AxiosResponse } from "axios";
import { AlertColor, Button, MenuItem, Paper, TextField, useMediaQuery} from "@mui/material";
import Title from "../../component/Title";
import { BaseControl, DropDownComboControl, DropDownComboOption } from "../../component/GenericFilter";
import { globalTheme } from "../../../common/theme";
import { useConstructor } from "../../../common/UseConstructor";
import { useAppSelector } from "../../../common/hooks";
import { RootState } from "../../../common/Store";
import { CommonProps } from "../../component/MainContents";
import { getUserPreferences, renewToken } from "../../../services/AuthService";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import { useNavigate } from "react-router-dom";
import { RegionCombo } from "../regions/RegionAccessories";
import { BranchInfo } from "../branches/BranchAccessories";
import { InvoiceFilingRecordViewer, InvoiceFilingRecordViewerHandle } from "./InvoiceFilingRecordViewer";
import { MonthNames } from "../../../common/Constants";

export function InvoiceFiling(props: CommonProps) {
	const [loading, setLoading] = React.useState(true);
	const [hasError, setHasError] = React.useState(false);
	const [regionComboData, setRegionComboData] = React.useState<DropDownComboOption[]>([]);

    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);

    const viewerModalRef = React.useRef<InvoiceFilingRecordViewerHandle>(null);

	const navigate = useNavigate();

    const style = { ...globalTheme() };

    const tinyScreen = useMediaQuery('(max-width:1366px)');
    const smallScreen = useMediaQuery('(max-width:1920px)');
    const normalScreen = useMediaQuery('(max-width:2560px)');

    const [controls, setControls] = React.useState<BaseControl[]>([]); //For filtering
	
	const [selectedRegion, setSelectedRegion] = React.useState<string>("");
	const [selectedYear, setSelectedYear] = React.useState<number>(new Date().getFullYear());
	const [selectedMonth, setSelectedMonth] = React.useState<number>(new Date().getMonth()+1);
	
	const [fileNo, setFileNo] = React.useState<number>(0);

    //Dialogs and snacks
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }

	function LoadData(poNoFrom: string, poNoTo: string) {
		setLoading(true);

	}

	useConstructor(() => Initialise())
    function Initialise() {
        setLoading(true);
		renewToken();

		let endUsersDropDownOption: DropDownComboOption[] = [];
		props.endUsers.map(e => endUsersDropDownOption.push({ name: e.endUserName, value: e.endUserId.toString() }));
		setControls([
			{
				key: 'customer',
				title: 'Customer',
				dropdownOptions: props.customers
			} as DropDownComboControl,
			{
				key: 'endUser',
				title: 'End User',
				dropdownOptions: endUsersDropDownOption
			} as DropDownComboControl
		]);

		axios.all([
			axios.get<any[]>(`Api/V1/Region/Combo`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			}),                
			axios.get<BranchInfo[]>(`Api/V1/Branch`, {
				headers: {
					'Content-Encoding': 'gzip'
				}
			})
		]).then(axios.spread((...responses) => {
			const regionComboResult: AxiosResponse<RegionCombo[]> = responses[0];
			const branchInfoResult: AxiosResponse<BranchInfo[]> = responses[1];

			let loadedRegions: DropDownComboOption[] = [];
			branchInfoResult.data.map(b => {
				if (regionComboResult.data.find(r => r.regionName === b.region))
					loadedRegions.push({ name: b.companyName1, value: b.region });
			});
			setRegionComboData(loadedRegions);

			let preferences = getUserPreferences();
			if (preferences)
			{
				setSelectedRegion(preferences.defaultRegion);
			}
			setFileNo(branchInfoResult.data.find(b => b.region === preferences?.defaultRegion)?.printIndex ?? 0);

			setLoading(false);

			LoadData("-", "-");
		})).catch((error) => {
			setHasError(true);
			setLoading(false);
			console.log(error);
			showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
		})

    }

    function Preview() {
		viewerModalRef.current?.Initialise(selectedRegion, selectedYear, selectedMonth);
	}

    return (
        <div className={style.PageMain}>
			<Title pageTitle="Invoice Filing Record" />
				<Paper variant="outlined" className={style.searchBar} >
					<div style={{ display: 'flex'}}>
						<div className={style.ModalFields} style={{width: '100%'}}>
							<TextField style={{ left: 10, width: 100, paddingTop: "5px", paddingBottom: "5px" }}
								onChange={(e) => {setSelectedRegion(e.target.value)}}
								value = {selectedRegion}
								select
								label="Region"
							>
								{regionComboData.map((item)=> (
									<MenuItem key={item.value} value={item.value}>
										{item.value}
									</MenuItem>
								))}
							</TextField>
							<div style={{ width: 10 }}></div>
							<TextField style={{ left: 10, width: 120, paddingTop: "5px", paddingBottom: "5px" }}
								onChange={(e) => {setSelectedYear(Number(e.target.value))}}
								value = {selectedYear}
								select
								label="Year"
							>
								<MenuItem key={new Date().getFullYear()-1} value={new Date().getFullYear()-1}>
									{new Date().getFullYear()-1}
								</MenuItem>
								<MenuItem key={new Date().getFullYear()} value={new Date().getFullYear()}>
									{new Date().getFullYear()}
								</MenuItem>
								<MenuItem key={new Date().getFullYear()+1} value={new Date().getFullYear()+1}>
									{new Date().getFullYear()+1}
								</MenuItem>
							</TextField>
							<div style={{ width: 10 }}></div>
							<TextField style={{ left: 10, width: 120, paddingTop: "5px", paddingBottom: "5px" }}
								onChange={(e) => {setSelectedMonth(Number(e.target.value))}}
								value = {selectedMonth}
								select
								label="Month"
							>
								{MonthNames.map((item) => (
		                          <MenuItem key={MonthNames.indexOf(item)+1} value={MonthNames.indexOf(item)+1}>{MonthNames.indexOf(item)+1}</MenuItem>
        		                ))}
							</TextField>
							<div style={{ flexGrow: 1 }}></div>
							<div style={{ width: '100px', paddingTop: "12px", paddingLeft: "8px" }}>
								<Button type="submit" aria-controls="filter-menu" aria-haspopup="true" variant="contained" color="secondary" fullWidth onClick={(e)=> Preview()}>
									Show
								</Button>
							</div>
						</div>
					</div>
				</Paper>
				<div className={style.PageContent}>
			</div>

			<InvoiceFilingRecordViewer ref={viewerModalRef} onClose={()=>{}}/>

			<SnackBarMessage {...snackSettings} />

        </div>
    );
}

